import { useState, useRef } from "react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Trash2, Copy, ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { ResizeHandle } from "./ResizeHandle";
import { ShapeBlock } from "./blocks/ShapeBlock";
import { IconBlock } from "./blocks/IconBlock";
import { CoverBlock } from "./blocks/CoverBlock";
import { TextBlock } from "./blocks/TextBlock";
import { ImageBlock } from "./blocks/ImageBlock";
import { DynamicImageBlock } from "./blocks/DynamicImageBlock";
import { SmartFieldBlock } from "./blocks/SmartFieldBlock";
import { VideoEmbedBlock } from "./blocks/VideoEmbedBlock";
import { ButtonBlock } from "./blocks/ButtonBlock";
import { PackageSelectorBlock } from "./blocks/PackageSelectorBlock";
import { LineItemsTable } from "./blocks/LineItemsTable";
import { SignatureBlock } from "./blocks/SignatureBlock";
import { GoodBetterBestBlock } from "./blocks/GoodBetterBestBlock";
import { TermsBlock } from "./blocks/TermsBlock";
import { MarketingGalleryBlock } from "./blocks/MarketingGalleryBlock";
import { TotalsBlock } from "./blocks/TotalsBlock";
import type { Block } from "@/types/proposals";
import { cn } from "@/lib/utils";

interface DraggableBlockProps {
  block: Block;
  isSelected: boolean;
  onSelect: (multiSelect: boolean) => void;
  onUpdate: (updates: Partial<Block>) => void;
  onDelete: () => void;
  onResize: (newRect: { x: number; y: number; width: number; height: number }) => void;
  onResizeEnd?: () => void;
  zoom: number;
  smartFieldData?: Record<string, any>;
}

const SHADOW_MAP = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
};

export function DraggableBlock({
  block,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  onResize,
  onResizeEnd,
  zoom,
  smartFieldData,
}: DraggableBlockProps) {
  const [isResizing, setIsResizing] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: block.id,
    data: { block },
    disabled: isResizing || block.type === 'cover', // Cover blocks are non-draggable
  });

  const style = block.style || {};
  
  // Guard: Ensure position exists
  if (!block.position) {
    console.error('Block missing position:', block);
    return null;
  }

  const handleResizeStart = (handle: string) => {
    setIsResizing(true);
  };

  const handleResize = (handle: string, delta: { x: number; y: number }) => {
    // Guard against undefined position
    if (!block.position) return;
    
    const currentX = block.position.x;
    const currentY = block.position.y;
    const currentWidth = block.position.width;
    const currentHeight = block.position.height === 'auto' ? 100 : block.position.height;

    let newX = currentX;
    let newY = currentY;
    let newWidth = currentWidth;
    let newHeight = currentHeight;

    const dx = delta.x / zoom;
    const dy = delta.y / zoom;

    // Horizontal handles
    if (handle.includes('e')) {
      // Dragging right edge
      newWidth = currentWidth + dx;
    }
    if (handle.includes('w')) {
      // Dragging left edge: move x and shrink/grow width from the left
      newX = currentX + dx;
      newWidth = currentWidth - dx;
    }

    // Vertical handles
    if (handle.includes('s')) {
      // Dragging bottom edge
      newHeight = currentHeight + dy;
    }
    if (handle.includes('n')) {
      // Dragging top edge: move y and shrink/grow height from the top
      newY = currentY + dy;
      newHeight = currentHeight - dy;
    }

    // Enforce minimums
    const minWidth = 50;
    const minHeight = 30;

    if (newWidth < minWidth) {
      // Clamp width and correct x if we shrank past minimum from the left
      if (handle.includes('w')) {
        newX = currentX + (currentWidth - minWidth);
      }
      newWidth = minWidth;
    }

    if (newHeight < minHeight) {
      if (handle.includes('n')) {
        newY = currentY + (currentHeight - minHeight);
      }
      newHeight = minHeight;
    }

    onResize({
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    });
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    onResizeEnd?.();
  };

  const renderBlockContent = () => {
    const commonProps = {
      block,
      onUpdate,
      onSelect: () => onSelect(false),
    };

    switch (block.type) {
      case "cover":
        return <CoverBlock {...commonProps} />;
      case "text":
        return <TextBlock {...commonProps} />;
      case "image":
        return <ImageBlock {...commonProps} />;
      case "dynamic_image":
        return <DynamicImageBlock {...commonProps} />;
      case "smart_field":
        return <SmartFieldBlock {...commonProps} />;
      case "video_embed":
        return <VideoEmbedBlock {...commonProps} />;
      case "button":
        return <ButtonBlock {...commonProps} />;
      case "package_selector":
        return <PackageSelectorBlock {...commonProps} />;
      case "line_items_table":
        return <LineItemsTable {...commonProps} smartFieldData={smartFieldData} />;
      case "signature":
        return <SignatureBlock {...commonProps} />;
      case "good_better_best":
        return <GoodBetterBestBlock {...commonProps} smartFieldData={smartFieldData} />;
      case "terms":
        return <TermsBlock {...commonProps} />;
      case "marketing_gallery":
        return <MarketingGalleryBlock {...commonProps} />;
      case "totals_summary":
        return <TotalsBlock {...commonProps} />;
      case "shape":
        return <ShapeBlock {...commonProps} />;
      case "icon":
        return <IconBlock {...commonProps} />;
      default:
        return <div className="p-4 text-sm text-muted-foreground">{block.type}</div>;
    }
  };

  // Positioning styles (outer container)
  const positionStyle = {
    position: 'absolute' as const,
    left: block.position.x,
    top: block.position.y,
    width: block.position.width,
    height: block.position.height === 'auto' ? 'auto' : block.position.height,
    zIndex: block.position.zIndex,
    transform: CSS.Transform.toString(transform),
    cursor: isResizing ? 'resizing' : 'default',
  };

  // Visual styles (inner wrapper)
  const visualStyle = {
    width: '100%',
    height: '100%',
    borderWidth: style.borderWidth,
    borderColor: style.borderColor,
    borderStyle: style.borderStyle || 'solid',
    borderRadius: style.borderRadius,
    boxShadow: SHADOW_MAP[style.boxShadow || 'none'],
    backgroundColor: style.backgroundColor,
    opacity: style.opacity,
    padding: style.padding,
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    color: style.textColor,
    lineHeight: style.lineHeight,
    textAlign: style.textAlign,
    overflow: 'hidden',
  };

  return (
    <div
      ref={setNodeRef}
      style={positionStyle}
      className={cn(
        "group transition-all",
        isSelected && "ring-1 ring-primary/30 shadow-sm",
        !isResizing && "hover:ring-1 hover:ring-primary/20"
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e.metaKey || e.ctrlKey);
      }}
    >
      <div ref={blockRef} style={visualStyle}>
        {renderBlockContent()}
      </div>

      {/* Hover hint for unselected blocks */}
      {!isSelected && block.type !== 'cover' && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/75 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            Click to select • Drag grip icon to move • Drag handles to resize
          </div>
        </div>
      )}

      {/* Cover block hint */}
      {!isSelected && block.type === 'cover' && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/75 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            Click to select • Edit in Style panel →
          </div>
        </div>
      )}

      {/* Floating Toolbar */}
      {isSelected && (
        <>
          {block.type === 'cover' ? (
            /* Cover block toolbar - simplified */
            <div className="absolute -top-10 left-0 flex items-center gap-1 bg-primary text-primary-foreground px-3 py-1 rounded shadow-lg z-50">
              <span className="text-xs font-medium">Cover Page (Edit in Style panel →)</span>
            </div>
          ) : (
            /* Regular block toolbar */
            <div className="absolute -top-10 left-0 flex items-center gap-1 bg-primary text-primary-foreground px-2 py-1 rounded shadow-lg z-50">
              <div
                {...listeners}
                {...attributes}
                className="h-6 px-1 cursor-move hover:bg-primary-foreground/20 rounded flex items-center justify-center"
                title="Drag to move block"
              >
                <GripVertical className="w-4 h-4 text-primary-foreground" />
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate({ position: { ...block.position, zIndex: 999 } });
                }}
                title="Bring to Front"
              >
                <ArrowUp className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={(e) => {
                e.stopPropagation();
                onUpdate({ position: { ...block.position, zIndex: 0 } });
              }}
              title="Send to Back"
            >
              <ArrowDown className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={(e) => {
                e.stopPropagation();
                // Duplicate functionality would go here
              }}
              title="Duplicate"
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Delete"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          )}
        </>
      )}

      {/* Resize Handles - hide for cover blocks */}
      {isSelected && block.type !== 'cover' && (
        <>
          <ResizeHandle position="nw" onResize={handleResize} onResizeStart={() => handleResizeStart('nw')} onResizeEnd={handleResizeEnd} />
          <ResizeHandle position="ne" onResize={handleResize} onResizeStart={() => handleResizeStart('ne')} onResizeEnd={handleResizeEnd} />
          <ResizeHandle position="sw" onResize={handleResize} onResizeStart={() => handleResizeStart('sw')} onResizeEnd={handleResizeEnd} />
          <ResizeHandle position="se" onResize={handleResize} onResizeStart={() => handleResizeStart('se')} onResizeEnd={handleResizeEnd} />
          <ResizeHandle position="n" onResize={handleResize} onResizeStart={() => handleResizeStart('n')} onResizeEnd={handleResizeEnd} />
          <ResizeHandle position="s" onResize={handleResize} onResizeStart={() => handleResizeStart('s')} onResizeEnd={handleResizeEnd} />
          <ResizeHandle position="w" onResize={handleResize} onResizeStart={() => handleResizeStart('w')} onResizeEnd={handleResizeEnd} />
          <ResizeHandle position="e" onResize={handleResize} onResizeStart={() => handleResizeStart('e')} onResizeEnd={handleResizeEnd} />
        </>
      )}
    </div>
  );
}
